import { Cloud } from '@cast/shared-types';
import {
  EdgeClusterAddon,
  EdgeLocation,
  EdgeLocationControlPlaneModeEnum,
} from '@cast/types-omni';
import isEmpty from 'lodash/isEmpty';
import _isPlainObject from 'lodash/isPlainObject';

import { notReachable } from '@cast/utils';

import {
  AddonFormValue,
  EdgeLocationAddonName,
  EdgeLocationFormValues,
} from './types';
import { CUSTOM_EDGE_PROVIDER } from './constants';

export const isEdgeLocationAddonEnabled = ({
  edgeLocation,
  addonName,
}: {
  edgeLocation: EdgeLocation;
  addonName: EdgeLocationAddonName;
}): boolean =>
  edgeLocation.edgeClusterSpec?.addons?.some(
    (addon) => addon.name === addonName
  ) || false;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  _isPlainObject(value);

type ParseAddonValuesTextResult =
  | { type: 'success'; addonValues?: Record<string, unknown> }
  | { type: 'error' };

export const parseAddonValuesText = (
  addonValuesText?: string
): ParseAddonValuesTextResult => {
  if (!addonValuesText?.trim()) {
    return { type: 'success' };
  }

  try {
    const parsedAddonValues: unknown = JSON.parse(addonValuesText);
    return isPlainObject(parsedAddonValues)
      ? { type: 'success', addonValues: parsedAddonValues }
      : { type: 'error' };
  } catch {
    return { type: 'error' };
  }
};

export const formModelAddonsToApiAddons = (
  addons: EdgeLocationFormValues['addons']
): EdgeClusterAddon[] =>
  Object.entries(addons)
    .filter(([, addon]) => addon.enabled)
    .map(([addonName, addon]) => {
      const parseResult = parseAddonValuesText(addon.values);

      switch (parseResult.type) {
        case 'success':
          return { name: addonName, values: parseResult.addonValues };
        case 'error':
          return { name: addonName };
        default:
          return notReachable(parseResult);
      }
    });

export const apiAddonsToFormModelAddons = (
  addons: EdgeClusterAddon[]
): EdgeLocationFormValues['addons'] =>
  Object.fromEntries(
    addons.map((addon): [string, AddonFormValue] => [
      addon.name,
      {
        enabled: true,
        values:
          addon.values && !isEmpty(addon.values)
            ? JSON.stringify(addon.values, null, 2)
            : undefined,
      },
    ])
  );

export const formModelToApi = (
  formValues: EdgeLocationFormValues
): EdgeLocation => {
  const addons: EdgeClusterAddon[] = formModelAddonsToApiAddons(
    formValues.addons
  );

  const basePayload: EdgeLocation = {
    name: formValues.locationName,
    description: formValues.description,
    controlPlaneMode: EdgeLocationControlPlaneModeEnum.SHARED,
    edgeClusterSpec: { addons },
  };

  const cloudPayload = { ...basePayload, region: formValues.region };

  if (formValues.provider === Cloud.aws) {
    return { ...cloudPayload, aws: {} };
  }

  if (formValues.provider === Cloud.gcp) {
    return { ...cloudPayload, gcp: { projectId: formValues.projectId } };
  }

  if (formValues.provider === Cloud.oracle) {
    return { ...cloudPayload, oci: {} };
  }

  if (formValues.provider === CUSTOM_EDGE_PROVIDER) {
    return { ...basePayload, custom: {} };
  }

  return basePayload;
};
